
import { useCallback, useEffect, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabaseAdmin } from "@/integrations/supabase/adminClient";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { UsersTable } from "./users/UsersTable";
import { UsersFilter } from "./users/UsersFilter";
import { AddUserDialog } from "./users/AddUserDialog";
import type { User, SortField, SortDirection } from "./users/types";

export function UsersOverview() {
  const { toast } = useToast();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterAdmin, setFilterAdmin] = useState<string>("all");
  const [sortField, setSortField] = useState<SortField>('email');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);
  const [isCreatingUser, setIsCreatingUser] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const { data: authUsers, error } = await supabaseAdmin.auth.admin.listUsers();
      
      if (error) throw error;

      if (!authUsers.users) {
        throw new Error('Geen gebruikersdata ontvangen');
      }

      const usersWithAdminStatus = await Promise.all(
        authUsers.users.map(async (user) => {
          const { data: isAdmin } = await supabaseAdmin.rpc('is_admin', {
            user_id: user.id,
          });

          return {
            id: user.id,
            email: user.email || '',
            created_at: user.created_at,
            last_sign_in_at: user.last_sign_in_at,
            is_admin: Boolean(isAdmin), // Explicitly convert to boolean
          };
        })
      );

      setUsers(usersWithAdminStatus);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast({
        title: "Fout bij ophalen gebruikers",
        description: "Er ging iets mis bij het ophalen van de gebruikers.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const toggleAdminStatus = async (userId: string, currentStatus: boolean) => {
    try {
      if (currentStatus) {
        await supabaseAdmin
          .from('admin_users')
          .delete()
          .eq('user_id', userId);
      } else {
        await supabaseAdmin
          .from('admin_users')
          .insert({ user_id: userId });
      }

      toast({
        title: "Admin status bijgewerkt",
        description: `Admin rechten ${currentStatus ? 'verwijderd' : 'toegevoegd'}.`,
      });

      fetchUsers();
    } catch (error) {
      toast({
        title: "Fout bij bijwerken admin status",
        description: "Er ging iets mis bij het bijwerken van de admin rechten.",
        variant: "destructive",
      });
    }
  };

  const deleteUser = async (userId: string) => {
    try {
      const { error } = await supabaseAdmin.auth.admin.deleteUser(userId);
      if (error) throw error;

      toast({
        title: "Gebruiker verwijderd",
        description: "De gebruiker is succesvol verwijderd.",
      });

      fetchUsers();
    } catch (error) {
      toast({
        title: "Fout bij verwijderen",
        description: "Er ging iets mis bij het verwijderen van de gebruiker.",
        variant: "destructive",
      });
    }
  };

  const createUser = async (email: string, password: string, isAdmin: boolean) => {
    try {
      setIsCreatingUser(true);

      const { data: userData, error: createError } = await supabaseAdmin.auth.admin.createUser({
        email: email,
        password: password,
        email_confirm: true
      });

      if (createError) throw createError;

      if (isAdmin && userData.user) {
        await supabaseAdmin
          .from('admin_users')
          .insert({ user_id: userData.user.id });
      }

      toast({
        title: "Gebruiker aangemaakt",
        description: "De nieuwe gebruiker is succesvol aangemaakt.",
      });

      setIsAddUserOpen(false);
      fetchUsers();
    } catch (error: any) {
      toast({
        title: "Fout bij aanmaken gebruiker",
        description: error.message || "Er ging iets mis bij het aanmaken van de gebruiker.",
        variant: "destructive",
      });
    } finally {
      setIsCreatingUser(false);
    }
  };

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(current => current === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredAndSortedUsers = [...users]
    .filter(user => {
      const matchesSearch = user.email.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesFilter = filterAdmin === "all" 
        || (filterAdmin === "admin" && user.is_admin)
        || (filterAdmin === "user" && !user.is_admin);
      return matchesSearch && matchesFilter;
    })
    .sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      
      if (aValue === null) return sortDirection === 'asc' ? 1 : -1;
      if (bValue === null) return sortDirection === 'asc' ? -1 : 1;
      
      return sortDirection === 'asc' 
        ? aValue < bValue ? -1 : 1
        : aValue > bValue ? -1 : 1;
    });

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Gebruikers</CardTitle>
        </CardHeader>
        <CardContent>
          Laden...
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <CardTitle>Gebruikers</CardTitle>
        <AddUserDialog
          isOpen={isAddUserOpen}
          onOpenChange={setIsAddUserOpen}
          onSubmit={createUser}
          isLoading={isCreatingUser}
        />
      </CardHeader>
      <CardContent>
        <UsersFilter
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          filterAdmin={filterAdmin}
          onFilterChange={setFilterAdmin}
        />
        <ScrollArea className="h-[400px] rounded-md border">
          <UsersTable
            users={filteredAndSortedUsers}
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={toggleSort}
            onToggleAdmin={toggleAdminStatus}
            onDeleteUser={deleteUser}
          />
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
